<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Tipos de Planos</h5>
      <b-button
        v-if="hasPermission('adicionar_tipos_planos')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Tipo de Plano"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por Nome"
        class="form-control"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        class="d-flex align-items-center"
        @click="limparFiltros"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('duracao')">
              Duração {{ arrow('duracao') }}
            </button>
          </th>
          <th>Descrição</th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Status {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="t in planos" :key="t.id">
          <td>{{ t.nome }}</td>
          <td>{{ mapDuracao(t.duracao) }}</td>
          <td>{{ t.descricao }}</td>
          <td>{{ t.ativo ? 'Ativo' : 'Inativo' }}</td>
          <td class="text-end">
            <router-link
              v-if="hasPermission('detalhar_tipos_planos')"
              :to="`/cadastros/tipos-planos/${t.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-2" title="Detalhar"></i>
            </router-link>
            <router-link
              v-if="hasPermission('alterar_tipos_planos')"
              :to="`/cadastros/tipos-planos/${t.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_tipos_planos')"
              class="bi bi-trash-fill text-danger mx-2"
              @click="excluir(t.id)"
              title="Excluir"
              role="button"
              tabindex="0"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

/**
 * mapDuracao:
 * - Mantém rótulos clássicos (Diário, Mensal, Anual, etc.)
 * - Converte automaticamente se for múltiplo de 365/30/7 → "X ano(s)", "X mês(es)", "X semana(s)"
 * - Fallback: "X dia(s)"
 */
function mapDuracao(d) {
  if (!d || d <= 0) return '-'

  const labels = {
    1: 'Diário',
    7: 'Semanal',
    14: 'Quinzenal',
    30: 'Mensal',
    60: 'Bimestral',
    90: 'Trimestral',
    120: 'Quadrimestral',
    180: 'Semestral',
    365: 'Anual'
  }
  if (labels[d]) return labels[d]

  // Inteligência para novas durações salvas em dias
  if (d % 365 === 0) return `${d / 365} anos`
  if (d % 30 === 0)  return `${d / 30} meses`
  if (d % 7 === 0)   return `${d / 7} semanas`
  return `${d} dias`
}

// composable de listagem/paginação/ordenação
const {
  items: planos,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/tipos-planos`,
  { key: 'nome', order: 'asc' }
)

const pageOptions = [10, 15, 20, 25, 50]
const startRow = computed(() => (page.value - 1) * limit.value + 1)
const endRow   = computed(() => Math.min(page.value * limit.value, totalItems.value))

watch(page,  (n, o) => n !== o && load())
watch(limit,(n, o) => { if (n !== o) { page.value = 1; load() }})

// filtros
function filtrar() {
  page.value = 1
  load()
}
function limparFiltros() {
  filters.value = { nome: '' }
  page.value = 1
  load()
}
function changeLimit() {
  page.value = 1
  load()
}

// CRUD e navegação
function irParaCadastro() {
  router.push('/cadastros/tipos-planos/novo')
}
async function excluir(id) {
  const ok = await proxy.$confirm('Excluir tipo de plano?', 'Confirmação')
  if (!ok) return
  await axios.delete(`/cadastros/tipos-planos/${id}`)
  proxy.$toast.success('Tipo de plano excluído com sucesso!')
  load()
}

onMounted(async () => {
  await fetchPermissions()
  filters.value = { nome: '' }
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
/* oculta ícone nativo */
.bv-table-sort-icon {
  display: none !important;
}
</style>
